/*
 * Vehicle Sharing Form Component
 * 
 * Allows vehicle owners to share their private vehicles with other users
 * Features:
 * - Email validation for existing users
 * - Weekly schedule configuration with time ranges
 * - Quick presets for common schedules (weekday commute)
 * - Notification preferences
 * - Optional message to shared user
 * - Invitation system for non-registered users
 * 
 * For carpooling scenarios where vehicle owners share location
 * with specific users on scheduled days/times
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/config';

/**
 * Weekly Schedule Grid Component - Enhanced with Multiple Time Ranges
 */
const WeeklyScheduleGrid = ({ schedule, onChange, shareAtAllTimes, onShareAtAllTimesChange }) => {
  const days = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }
  ];

  const handleDayChange = (day, rangeIndex, field, value) => {
    const updatedSchedule = { ...schedule };
    
    if (!updatedSchedule[day]) {
      updatedSchedule[day] = { enabled: true, time_ranges: [] };
    }
    
    if (!updatedSchedule[day].time_ranges) {
      updatedSchedule[day].time_ranges = [];
    }
    
    // Ensure the range exists
    while (updatedSchedule[day].time_ranges.length <= rangeIndex) {
      updatedSchedule[day].time_ranges.push({ start_time: '07:00', end_time: '18:00' });
    }
    
    updatedSchedule[day].time_ranges[rangeIndex] = {
      ...updatedSchedule[day].time_ranges[rangeIndex],
      [field]: value
    };
    
    onChange(updatedSchedule);
  };

  const toggleDay = (day) => {
    const updatedSchedule = { ...schedule };
    
    if (updatedSchedule[day]) {
      // Remove the day
      delete updatedSchedule[day];
    } else {
      // Add the day with one default time range
      updatedSchedule[day] = {
        enabled: true,
        time_ranges: [{ start_time: '07:00', end_time: '18:00' }]
      };
    }
    
    onChange(updatedSchedule);
  };

  const addTimeRange = (day) => {
    const updatedSchedule = { ...schedule };
    
    if (!updatedSchedule[day]) {
      updatedSchedule[day] = { enabled: true, time_ranges: [] };
    }
    
    if (!updatedSchedule[day].time_ranges) {
      updatedSchedule[day].time_ranges = [];
    }
    
    // Add a new time range
    updatedSchedule[day].time_ranges.push({ start_time: '07:00', end_time: '18:00' });
    
    onChange(updatedSchedule);
  };

  const removeTimeRange = (day, rangeIndex) => {
    const updatedSchedule = { ...schedule };
    
    if (updatedSchedule[day] && updatedSchedule[day].time_ranges) {
      updatedSchedule[day].time_ranges.splice(rangeIndex, 1);
      
      // If no more time ranges, remove the day
      if (updatedSchedule[day].time_ranges.length === 0) {
        delete updatedSchedule[day];
      }
    }
    
    onChange(updatedSchedule);
  };

  const applyPreset = (presetType) => {
    let presetSchedule = {};
    
    if (presetType === 'weekday_commute') {
      // Monday to Friday, 7-8 AM & 5-6 PM (two time ranges)
      ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].forEach(day => {
        presetSchedule[day] = {
          enabled: true,
          time_ranges: [
            { start_time: '07:00', end_time: '08:00' },
            { start_time: '17:00', end_time: '18:00' }
          ]
        };
      });
    } else if (presetType === 'all_weekdays') {
      // Monday to Friday, all day (one time range)
      ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].forEach(day => {
        presetSchedule[day] = {
          enabled: true,
          time_ranges: [
            { start_time: '00:00', end_time: '23:59' }
          ]
        };
      });
    } else if (presetType === 'weekends') {
      // Saturday and Sunday, all day (one time range)
      ['saturday', 'sunday'].forEach(day => {
        presetSchedule[day] = {
          enabled: true,
          time_ranges: [
            { start_time: '00:00', end_time: '23:59' }
          ]
        };
      });
    }
    
    onChange(presetSchedule);
  };

  return (
    <div className="space-y-4">
      {/* Share at All Times Toggle */}
      <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div>
          <h4 className="font-medium text-blue-900">Share at All Times</h4>
          <p className="text-sm text-blue-700">Allow access 24/7 without schedule restrictions</p>
        </div>
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={shareAtAllTimes}
            onChange={(e) => onShareAtAllTimesChange(e.target.checked)}
            className="sr-only"
          />
          <div className={`relative w-10 h-6 transition-colors rounded-full ${
            shareAtAllTimes ? 'bg-blue-600' : 'bg-gray-300'
          }`}>
            <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
              shareAtAllTimes ? 'transform translate-x-4' : ''
            }`} />
          </div>
        </label>
      </div>

      {!shareAtAllTimes && (
        <>
          {/* Quick Presets */}
          <div className="space-y-2">
            <h4 className="font-medium text-gray-900">Quick Presets</h4>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => applyPreset('weekday_commute')}
                className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-md hover:bg-green-200 transition-colors"
              >
                📅 Weekday Commute (7-8 AM & 5-6 PM)
              </button>
              <button
                type="button"
                onClick={() => applyPreset('all_weekdays')}
                className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-md hover:bg-blue-200 transition-colors"
              >
                💼 All Weekdays
              </button>
              <button
                type="button"
                onClick={() => applyPreset('weekends')}
                className="px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-md hover:bg-purple-200 transition-colors"
              >
                🌴 Weekends Only
              </button>
            </div>
          </div>

          {/* Weekly Schedule Grid */}
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900">Weekly Schedule</h4>
            <div className="text-sm text-gray-600 mb-3">
              💡 Tip: You can add multiple time ranges per day (e.g., morning commute + evening commute)
            </div>
            
            {days.map(day => (
              <div key={day.key} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!schedule[day.key]}
                      onChange={() => toggleDay(day.key)}
                      className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300"
                    />
                    <span className="font-medium text-gray-900">{day.label}</span>
                  </label>
                  
                  {schedule[day.key] && (
                    <button
                      type="button"
                      onClick={() => addTimeRange(day.key)}
                      className="text-green-600 hover:text-green-800 text-sm px-2 py-1 border border-green-600 rounded hover:bg-green-50 transition-colors"
                    >
                      ➕ Add Time Range
                    </button>
                  )}
                </div>
                
                {schedule[day.key] && schedule[day.key].time_ranges && (
                  <div className="space-y-3">
                    {schedule[day.key].time_ranges.map((timeRange, index) => (
                      <div key={index} className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg border">
                        <div className="text-sm text-gray-600 min-w-0">
                          Range {index + 1}:
                        </div>
                        
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Start Time
                          </label>
                          <input
                            type="time"
                            value={timeRange.start_time || '07:00'}
                            onChange={(e) => handleDayChange(day.key, index, 'start_time', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                        
                        <div className="text-gray-400">to</div>
                        
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            End Time
                          </label>
                          <input
                            type="time"
                            value={timeRange.end_time || '18:00'}
                            onChange={(e) => handleDayChange(day.key, index, 'end_time', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                        
                        {schedule[day.key].time_ranges.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeTimeRange(day.key, index)}
                            className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 transition-colors"
                            title="Remove this time range"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Main Vehicle Sharing Form Component
 */
const VehicleShareForm = ({ 
  vehicle, 
  onClose, 
  onSuccess, 
  onStart,    // NEW: Called when submission starts
  onError,    // NEW: Called on errors
  existingShare = null, 
  mode = 'create' 
}) => {
  const [formData, setFormData] = useState({
    shared_with_email: '',
    schedule: {},
    share_at_all_times: false,
    send_notification: true,
    message: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Initialize form data with existing share if in edit mode
  useEffect(() => {
    if (mode === 'edit' && existingShare) {
      setFormData({
        shared_with_email: existingShare.shared_with_email || '',
        schedule: existingShare.schedule || {},
        share_at_all_times: existingShare.share_at_all_times || false,
        send_notification: existingShare.send_notification !== undefined ? existingShare.send_notification : true,
        message: existingShare.message || ''
      });
    }
  }, [mode, existingShare]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Call onStart callback for immediate feedback
    if (onStart) {
      onStart();
    }
    
    setLoading(true);
    setError('');

    try {
      const payload = {
        vehicle_id: vehicle.id,
        shared_with_email: formData.shared_with_email,
        schedule: formData.schedule,
        share_at_all_times: formData.share_at_all_times,
        send_notification: formData.send_notification,
        message: formData.message,
        temporarily_disabled: false
      };

      let response;
      if (mode === 'edit' && existingShare) {
        // Update existing share
        response = await axios.put(`${API}/vehicles/shares/${existingShare.id}`, payload);
        
        // For updates, create a success response format
        const successResponse = {
          message: "Vehicle share updated successfully",
          share: existingShare,
          invitation_sent: false,
          recipient_email: formData.shared_with_email
        };
        
        if (onSuccess) {
          onSuccess(successResponse);
        }
      } else {
        // Create new share
        response = await axios.post(`${API}/vehicles/${vehicle.id}/share`, payload);
        
        // Call success callback with response data
        if (onSuccess) {
          onSuccess({
            ...response.data,
            recipient_email: formData.shared_with_email
          });
        }
      }

      // Log success for debugging
      
    } catch (error) {
      
      let errorMessage = 'Failed to share vehicle. Please try again.';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      
      // Call error callback
      if (onError) {
        onError({
          message: errorMessage,
          originalError: error
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">
              {mode === 'edit' ? '✏️ Edit Vehicle Share' : '🤝 Share Vehicle'}
            </h2>
            <button
              onClick={onClose}
              disabled={loading}
              className="text-gray-500 hover:text-gray-700 text-2xl disabled:opacity-50"
            >
              ✕
            </button>
          </div>
          <p className="text-gray-600 mt-2">
            {mode === 'edit' 
              ? `Update sharing settings for ${vehicle.year} ${vehicle.make} ${vehicle.model}`
              : `Share your ${vehicle.year} ${vehicle.make} ${vehicle.model} with someone`
            }
          </p>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Show loading state when submitting */}
          {loading && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
                <span className="text-blue-700 font-medium">
                  {mode === 'edit' ? 'Updating vehicle share...' : 'Processing vehicle share...'}
                </span>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-center">
                <span className="text-red-600 mr-2">⚠️</span>
                <span className="text-red-700">{error}</span>
              </div>
            </div>
          )}

          {/* Email Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Share with Email Address *
            </label>
            <input
              type="email"
              value={formData.shared_with_email}
              onChange={(e) => setFormData({ ...formData, shared_with_email: e.target.value })}
              disabled={mode === 'edit' || loading}
              placeholder="Enter email address"
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              {mode === 'edit' 
                ? 'Email address cannot be changed when editing'
                : 'If the user doesn\'t have an account, they\'ll receive an invitation email'
              }
            </p>
          </div>

          {/* Schedule Configuration */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Sharing Schedule
            </label>
            <WeeklyScheduleGrid
              schedule={formData.schedule}
              onChange={(schedule) => setFormData({ ...formData, schedule })}
              shareAtAllTimes={formData.share_at_all_times}
              onShareAtAllTimesChange={(value) => setFormData({ ...formData, share_at_all_times: value })}
            />
          </div>

          {/* Notification Checkbox */}
          <div className="mb-6 flex items-center">
            <input
              type="checkbox"
              id="send_notification"
              checked={formData.send_notification}
              onChange={(e) => setFormData({ ...formData, send_notification: e.target.checked })}
              className="h-4 w-4 text-blue-600 rounded border-gray-300"
            />
            <label htmlFor="send_notification" className="ml-2 text-sm text-gray-700">
              Send email notification to the user
            </label>
          </div>

          {/* Optional Message */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message (Optional)
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="3"
              placeholder="Let them know about carpooling schedule, pickup location, etc."
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.shared_with_email.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {mode === 'edit' ? 'Updating...' : 'Sharing...'}
                </>
              ) : (
                <>
                  <span className="mr-2">{mode === 'edit' ? '✏️' : '🤝'}</span>
                  {mode === 'edit' ? 'Update Share' : 'Share Vehicle'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VehicleShareForm;